import {ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import { PlanCoordinate } from '../../../services/plan-coordinate';
import { PlanStepPage } from '../../../services/PlanStepPage';
import { DeliverableStepComponent } from '../deliverable-step.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { EditOverlayComponent } from '../edit-overlay/edit-overlay.component';
import { ITheoryOfActionInitiative } from '../../../services/ITheoryOfActionInitiative';
import { PlanStep } from '../../../services/PlanStep';
import { StrtPriority } from '../../../../shared/models/plan/StrtPriority';
import { SelectItem } from 'primeng/components/common/selectitem';
import { DomSanitizer } from '@angular/platform-browser';
import { UiUtils } from '../../../services/ui-utils';
import { InitiativeService } from '../../../services/initiative.service';
import { IDeliverableHeadings } from '../../../services/IDeliverableHeadings';
import { IPriorityDropdownOptionValue, PriorityDropdownOption } from '../../../../shared/models/IPriorityDropdownOption';
import { PriorityDropdownComponent } from '../../../../shared/components/priority-dropdown/priority-dropdown.component';
import { DataUtils } from '../../../../shared/utilities/data-utils';
import { ResourceService } from '../../../services/resource.service';
import {StrategiesDataStoreService} from '../../../../shared/services/strategies-data-store.service';
import {StrtInitiative} from '../../../../shared/models/plan/StrtInitiative';
import {Subscription} from 'rxjs/index';


@Component({
  selector: 'app-theory-of-action',
  templateUrl: './theory-of-action.component.html',
  styleUrls: ['./theory-of-action.component.scss']
})
export class TheoryOfActionComponent extends DeliverableStepComponent implements OnInit, OnDestroy {
  @ViewChild(PriorityDropdownComponent) dropdownComp: PriorityDropdownComponent;
  @ViewChild(EditOverlayComponent) editOverlayComp: EditOverlayComponent;
  @Input() headings: IDeliverableHeadings;
  @Input() strategyId: number;
  @Output() requestInitiatives = new EventEmitter<{startDate: Date, endDate?: Date}>();
  @Output() requestInitiativeDetails = new EventEmitter<{initiativeId: number}>();
  @Output() planCoordinateChange = new EventEmitter<PlanCoordinate>();
  private _theoryOfActionForm: FormGroup;
  // The Theory of Action component has a view state where there is not selected initiative.
  // This provides support for printing a blank form that the user may print and fill in manually.
  private _initiativeLoaded = false;
  private _selectedInitiative = InitiativeService.initializeTheoryOfActionInitiative({} as ITheoryOfActionInitiative);
  private _selectedInitiativeId: number;

  private _priorities: StrtPriority[] = [];
  private _hasInitiatives = false;
  private _subscriptions: Subscription[] = [];

  dropdownPriorityInitiatives: PriorityDropdownOption[] = [];

  availableInitiatives: SelectItem[] = [];

  constructor(private formBuilder: FormBuilder,
              private sanitizer: DomSanitizer,
              protected cdr: ChangeDetectorRef,
              _resourceService: ResourceService,
              private _strtDataStore: StrategiesDataStoreService) {
    super(PlanCoordinate.stepPageBy(PlanStepPage.TheoryOfAction), cdr, _resourceService);
    this.createForm();
    this.loadDropdown();
  }

  ngOnInit() {
    this._subscriptions.push(this._strtDataStore.toaAutoSelectInitiative$.subscribe(
      (initiative: StrtInitiative) => {
        if (initiative) {
          this.dropdownComp.selectItemWithID(initiative.initiativeId);
          this._strtDataStore.toaAutoSelectInitiative = null;
        }
    }));
    this.loadDropdown();
    this.reset();
  }

  ngOnDestroy(): void {
    if (this._subscriptions) {
      this._subscriptions.forEach((sub: Subscription) => {
        sub.unsubscribe();
      });
    }
  }

  @Input() set priorities(newPriorities: StrtPriority[]) {
    this._priorities = newPriorities;
    if (this._priorities) {
      this.availableInitiatives = DataUtils.reshapePriorities(this._priorities);
      this.dropdownPriorityInitiatives = PriorityDropdownOption.fromPriorityInitiatives(this._priorities);
      this.cdr.detectChanges();
    } else {
      this.availableInitiatives = [];
      this.dropdownPriorityInitiatives = [];
    }
  }

  get selectedInitiative(): ITheoryOfActionInitiative {
    return this._selectedInitiative;
  }

  @Input() set selectedInitiative(selectedInitiative: ITheoryOfActionInitiative) {
    this._selectedInitiative = selectedInitiative;
    if (this.selectedInitiative.initiative) {
      this._initiativeLoaded = true;
      this.headings = this.selectedInitiative.headingDTO;
    }
    this.cdr.detectChanges();
  }

  @Input() set hasInitiatives(value: boolean) {
    this._hasInitiatives = value;
    if (this._hasInitiatives) {
      this.selectedInitiativeField.enable();
    } else {
      this.selectedInitiativeField.disable();
    }
  }

  // =========================================================================
  // Form and form field convenience accessors

  get theoryOfActionForm() {
    return this._theoryOfActionForm;
  }

  get selectedInitiativeField() {
    return this._theoryOfActionForm.get('selectedInitiative');
  }

  // =========================================================================
  // accessors that are sensitive to the has selected initiative flag

  get hasSelectedInitiative() {
    return this._initiativeLoaded;
  }

  // =========================================================================
  // convenience accessors

  get hasResourcesPlaceholder() {
    return this.otherResources.length === 0;
  }

  get hasKeyMeasuresPlaceholder() {
    return this.measures.length === 0;
  }

  get hasOutcomesPlaceholder() {
    return this.outcomes.length === 0;
  }

  // =========================================================================
  // business value convenience accessors

  get missionDescription() {
    return this.sanitizer.bypassSecurityTrustHtml(this.selectedInitiative.mission);
  }

  get hasNoMission() {
    return this.selectedInitiative.mission.length === 0;
  }

  get visionDescription() {
    return this.sanitizer.bypassSecurityTrustHtml(this.selectedInitiative.vision);
  }

  get hasNoVision() {
    return this.selectedInitiative.vision.length === 0;
  }

  get otherResources() {
    return this.selectedInitiative.otherResources;
  }

  get totalFinancialResources() {
    return this.selectedInitiative.totalFinancialResources;
  }

  get totalFundingSources(): string {
    return this.selectedInitiative.totalSourceOfFunding;
  }

  get hasNoResources() {
    return this.otherResources.length === 0;
  }

  get measures() {
    return this.selectedInitiative.measures;
  }

  get hasNoMeasures() {
    return this.measures.length === 0;
  }

  get initiative() {
    return this.selectedInitiative.initiative;
  }

  get hasNoInitiative() {
    return this.initiative === '';
  }

  get objective() {
    return this.selectedInitiative.objective;
  }

  get hasNoObjective() {
    return this.objective.length === 0;
  }

  get outcomes() {
    return this.selectedInitiative.outcomes;
  }

  get hasNoOutcomes() {
    return this.outcomes.length === 0;
  }

  // =========================================================================
  // functions to support the color of the vertical bar in the ui

  get vbarBaseColor() {
    return InitiativeService.VBAR_BASE_COLOR;
  }

  get initiativeBorderHeight() {
    return this.vbarPrimaryColor !== this.vbarBaseColor ? '2px' : '0';
  }

  get vbarPrimaryColor() {
    return this.selectedInitiative.priorityColor;
  }

  get vbarDark20Color() {
    return this.hasSelectedInitiative ? UiUtils.darken(this.vbarPrimaryColor, 20) : this.vbarPrimaryColor;
  }

  get vbarDark40Color() {
    return this.hasSelectedInitiative ? UiUtils.darken(this.vbarPrimaryColor, 40) : this.vbarPrimaryColor;
  }

  get vbarLight20Color() {
    return this.hasSelectedInitiative ? UiUtils.lighten(this.vbarPrimaryColor, 20) : this.vbarPrimaryColor;
  }

  // =========================================================================
  // callback functions

  onSelectInitiative(selection: IPriorityDropdownOptionValue) {
    if (selection) {
      this._selectedInitiativeId = selection.primaryId;
      this.requestInitiativeDetails.emit({initiativeId: this._selectedInitiativeId});
    } else {
      throw new Error(`Could not determine initiative selection in theory of action`);
    }
  }

  onPlanCoordinateChangePurposeMissionBeliefs() {
    this.planCoordinateChange.emit(PlanCoordinate.stepBy(PlanStep.SYNTHESIZE_RESULTS));
  }

  onPlanCoordinateChangeEvaluating() {
    this.planCoordinateChange.emit(PlanCoordinate.stepBy(PlanStep.DETERMINE_IMPACT_OF_CRITICAL_INITIATIVES));
  }

  onPlanCoordinateChangeCreatingObjectivesAndCriticalInitiatives() {
    this.planCoordinateChange.emit(PlanCoordinate.stepBy(PlanStep.CREATE_OBJECTIVES_AND_CRITICAL_INITIATIVES));
  }

  onPlanCoordinateChangeIdentifyOutcomesAndKeyMeasures() {
    this.planCoordinateChange.emit(PlanCoordinate.stepBy(PlanStep.IDENTIFY_OUTCOMES_AND_KEY_MEASURES));
  }

  onPlanCoordinateChangeDesignImplementationPlan() {
    this.planCoordinateChange.emit(PlanCoordinate.stepBy(PlanStep.DESIGN_THE_IMPLEMENTATION_PLAN));
  }

  // =========================================================================
  //

  loadDropdown() {
    this.requestInitiatives.emit({startDate: undefined, endDate: undefined});
  }

  reset() {
    this._initiativeLoaded = false;
    this._selectedInitiativeId = undefined;
    this._selectedInitiative = InitiativeService.initializeTheoryOfActionInitiative({} as ITheoryOfActionInitiative);
    this._theoryOfActionForm.reset();
    this.selectedInitiativeField.reset();
    this.dropdownComp.clearSelections();
  }

  private createForm() {
    const validatorArr = [
      Validators.required
    ];
    this._theoryOfActionForm = this.formBuilder.group({
      selectedInitiative: [{value: undefined, disabled: true}, validatorArr]
    });
  }

  dropdownValueChanged(event) {
    this.onSelectInitiative(event);
  }
}
